// Which extra fields the Products and Stock lists show (docs/plans/list-fields.md). A list
// always shows its locked fields; up to MAX_FIELDS more are picked from the fields the
// business uses (its product field groups). The choice is kept on this device, per business,
// in localStorage like the sidebar choice: no sync, no migration.
import { useSyncExternalStore } from 'react'
import type { GroupId } from './catalog.ts'

export type ListName = 'products' | 'stock'

export type FieldId =
  | 'category'
  | 'stock'
  | 'brand'
  | 'shelf'
  | 'expiry'
  | 'mrp'
  | 'wholesale'
  | 'cost'
  | 'unit'
  | 'supplier'
  | 'barcode'
  | 'hs'
  | 'tags'
  | 'sku'
  | 'alert'
  | 'value'

export interface ListField {
  id: FieldId
  label: string
  hint?: string
  /** Offered only while this product field group is on. */
  group?: GroupId
}

export const MAX_FIELDS = 3

const FIELD: Record<FieldId, ListField> = {
  category: { id: 'category', label: 'Category' },
  stock: { id: 'stock', label: 'Stock count', hint: 'Its own column' },
  brand: { id: 'brand', label: 'Brand', group: 'brand' },
  shelf: { id: 'shelf', label: 'Shelf', group: 'shelf' },
  expiry: { id: 'expiry', label: 'Next expiry', hint: 'Batch and date', group: 'batches' },
  mrp: { id: 'mrp', label: 'MRP', group: 'pricing' },
  wholesale: { id: 'wholesale', label: 'Wholesale price', group: 'wholesale' },
  cost: { id: 'cost', label: 'Cost price' },
  unit: { id: 'unit', label: 'Unit' },
  supplier: { id: 'supplier', label: 'Supplier' },
  barcode: { id: 'barcode', label: 'Barcode' },
  hs: { id: 'hs', label: 'HS code', group: 'trade' },
  tags: { id: 'tags', label: 'Tags' },
  sku: { id: 'sku', label: 'SKU', hint: 'Under the name' },
  alert: { id: 'alert', label: 'Alert at' },
  value: { id: 'value', label: 'Stock value', hint: 'At cost' },
}

/** Each list's fields, in the order a phone card's line takes them. */
export const LIST_FIELDS: Record<ListName, ListField[]> = {
  products: (
    [
      'category',
      'stock',
      'brand',
      'shelf',
      'expiry',
      'mrp',
      'wholesale',
      'cost',
      'unit',
      'supplier',
      'barcode',
      'hs',
      'tags',
      'sku',
    ] as const
  ).map((id) => FIELD[id]),
  stock: (
    ['sku', 'alert', 'shelf', 'brand', 'category', 'supplier', 'value', 'expiry'] as const
  ).map((id) => FIELD[id]),
}

/** Shown whatever is chosen. */
export const LOCKED_FIELDS: Record<ListName, { label: string; hint?: string }[]> = {
  products: [
    { label: 'Name' },
    { label: 'Price' },
    { label: 'Status', hint: 'Shows the count when low or out' },
  ],
  stock: [{ label: 'Name' }, { label: 'In stock' }, { label: 'Status' }],
}

/** What each list showed before the choice existed. */
export const DEFAULT_FIELDS: Record<ListName, FieldId[]> = {
  products: ['stock', 'sku', 'category'],
  stock: ['sku', 'alert'],
}

/** The fields this business can pick for a list. */
export function offeredFields(list: ListName, groups: ReadonlySet<GroupId>): ListField[] {
  return LIST_FIELDS[list].filter((f) => !f.group || groups.has(f.group))
}

/**
 * The saved choice made safe: unknown or no-longer-offered fields dropped, at most
 * MAX_FIELDS, in the list's own order. Nothing saved (or unreadable) gives the defaults.
 */
export function cleanFields(
  list: ListName,
  saved: string | null,
  groups: ReadonlySet<GroupId>,
): FieldId[] {
  let picked: unknown
  try {
    picked = saved === null ? null : JSON.parse(saved)
  } catch {
    picked = null
  }
  const ids = Array.isArray(picked) ? picked : DEFAULT_FIELDS[list]
  const offered = offeredFields(list, groups)
  return offered
    .filter((f) => ids.includes(f.id))
    .slice(0, MAX_FIELDS)
    .map((f) => f.id)
}

/** Ticks or unticks a field; a tick past MAX_FIELDS is ignored. */
export function toggleField(current: FieldId[], id: FieldId, on: boolean): FieldId[] {
  if (!on) return current.filter((f) => f !== id)
  if (current.includes(id) || current.length >= MAX_FIELDS) return current
  return [...current, id]
}

/** The chosen fields in the list's order (for a phone card's line). */
export function inListOrder(list: ListName, fields: FieldId[]): FieldId[] {
  return LIST_FIELDS[list].filter((f) => fields.includes(f.id)).map((f) => f.id)
}

const key = (tenantId: string, list: ListName) => `ims.fields.${tenantId}.${list}`
const listeners = new Set<() => void>()

function read(storageKey: string): string | null {
  try {
    return localStorage.getItem(storageKey)
  } catch {
    return null
  }
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** The list's chosen fields on this device, and the means to change them. */
export function useListFields(list: ListName, tenantId: string, groups: ReadonlySet<GroupId>) {
  const storageKey = key(tenantId, list)
  const saved = useSyncExternalStore(subscribe, () => read(storageKey))
  const fields = cleanFields(list, saved, groups)
  const save = (value: string | null) => {
    try {
      if (value === null) localStorage.removeItem(storageKey)
      else localStorage.setItem(storageKey, value)
    } catch {
      // Storage blocked: nothing is kept, the defaults stay.
    }
    for (const l of listeners) l()
  }
  return {
    fields,
    offered: offeredFields(list, groups),
    has: (id: FieldId) => fields.includes(id),
    toggle: (id: FieldId, on: boolean) => save(JSON.stringify(toggleField(fields, id, on))),
    reset: () => save(null),
  }
}

export type ListFieldsApi = ReturnType<typeof useListFields>
